import React, { type ComponentType, type ReactNode, memo, type ReactElement } from 'react';
import isEqual from 'lodash/isEqual';
import {
	CORE_PROJECT,
	SERVICE_DESK_PROJECT,
	SOFTWARE_PROJECT,
	PRODUCT_DISCOVERY_PROJECT,
	CUSTOMER_SERVICE_PROJECT,
} from '@atlassian/jira-common-constants/src/project-types.tsx';
import {
	useProject,
	type UseProjectReturn,
} from '@atlassian/jira-navigation-apps-sidebar-common/src/controllers/project/index.tsx';
import { Skeleton } from '@atlassian/jira-navigation-apps-sidebar-common/src/ui/skeleton/index.tsx';
import AsyncSidebarConnect from '@atlassian/jira-navigation-apps-sidebar-connect/src/async.tsx';
import { useGlobalSettingsSystemConnectItems } from '@atlassian/jira-navigation-apps-sidebar-connect/src/controllers/global-settings-system/index.tsx';
import { useGlobalSettingsConnectItems } from '@atlassian/jira-navigation-apps-sidebar-connect/src/controllers/global-settings/index.tsx';
import { useGlobalConnectItems } from '@atlassian/jira-navigation-apps-sidebar-connect/src/controllers/global/index.tsx';
import {
	useInsight,
	useInsightObjectSchemaSidebar,
} from '@atlassian/jira-navigation-apps-sidebar-connect/src/controllers/insight/index.tsx';
import { useProjectReportsConnectItems } from '@atlassian/jira-navigation-apps-sidebar-connect/src/controllers/project-reports/index.tsx';
import { useProjectSettingsConnectItems } from '@atlassian/jira-navigation-apps-sidebar-connect/src/controllers/project-settings/index.tsx';
import { useProjectConnectItems } from '@atlassian/jira-navigation-apps-sidebar-connect/src/controllers/project/index.tsx';
import AsyncSidebarCore from '@atlassian/jira-navigation-apps-sidebar-core/src/async.tsx';
import AsyncSidebarForgeGlobalPage from '@atlassian/jira-navigation-apps-sidebar-forge-global-page/src/async.tsx';
import { useForgeGlobalPageItems } from '@atlassian/jira-navigation-apps-sidebar-forge-global-page/src/controllers/global/index.tsx';
import { genericConnectSidebars } from '@atlassian/jira-navigation-apps-sidebar-generic-sidebars/src/ui/index.tsx';
import AsyncSidebarGlobalSettingsApps from '@atlassian/jira-navigation-apps-sidebar-global-settings-apps/src/async.tsx';
import AsyncSidebarGlobalSettingsSystem from '@atlassian/jira-navigation-apps-sidebar-global-settings-system/src/async.tsx';
import AsyncSidebarInsightObjectSchema from '@atlassian/jira-navigation-apps-sidebar-insight-object-schema/src/async.tsx';
import AsyncSidebarNextGen from '@atlassian/jira-navigation-apps-sidebar-next-gen/src/async.tsx';
import AsyncSidebarPlan from '@atlassian/jira-navigation-apps-sidebar-plan/src/async.tsx';
import AsyncSidebarPolaris from '@atlassian/jira-navigation-apps-sidebar-polaris/src/async.tsx';
import AsyncSidebarServiceDesk from '@atlassian/jira-navigation-apps-sidebar-servicedesk/src/async.tsx';
import AsyncSidebarSoftwareClassic from '@atlassian/jira-navigation-apps-sidebar-software-classic/src/async.tsx';
import AsyncSidebarUser from '@atlassian/jira-navigation-apps-sidebar-user/src/async.tsx';
import { connectGeneralResources } from '@atlassian/jira-router-resources-connect-general/src/index.tsx';
import { useResource } from '@atlassian/react-resource-router';

const renderProjectSidebar = ({ data: project, loading }: UseProjectReturn) => {
	if (loading) {
		return <Skeleton isFull />;
	}

	if (!project) {
		return <AsyncSidebarUser />;
	}

	switch (project.type) {
		case SERVICE_DESK_PROJECT:
		case CUSTOMER_SERVICE_PROJECT:
			return <AsyncSidebarServiceDesk />;
		case PRODUCT_DISCOVERY_PROJECT:
			return <AsyncSidebarPolaris />;
		case SOFTWARE_PROJECT:
			if (project.isSimplified) {
				return <AsyncSidebarNextGen />;
			}
			return <AsyncSidebarSoftwareClassic />;
		// delete when cleaning up getWillShowNav4
		case CORE_PROJECT:
			return <AsyncSidebarCore />;
		default:
			// TODO: render error
			return null;
	}
};

// Since argument of `renderProjectSidebar` is similar to `props` object of a React component
// so we can convert normal function to functional component
const ProjectSidebar = memo(renderProjectSidebar, isEqual);

export const SidebarSelectorProvider = ({
	sidebar,
	children,
}: {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	sidebar: ComponentType<Record<any, any>> | ComponentType<Record<any, any>>[] | null;
	children: (sb: ReactNode) => ReactElement | null;
}) => {
	const projectState = useProject();
	const isInsightMenuSelected = useInsight();
	const shouldRenderInsightObjectSchemaSidebar = useInsightObjectSchemaSidebar();

	const { selected: pSelected, loading: pLoading } = useProjectConnectItems();
	const { selected: prSelected, loading: prLoading } = useProjectReportsConnectItems();
	const { selected: psSelected, loading: psLoading } = useProjectSettingsConnectItems();
	const { selected: gSelected, loading: gLoading } = useGlobalConnectItems();
	const { selected: gsSelected, loading: gsLoading } = useGlobalSettingsConnectItems();
	const { selected: gssSelected, loading: gssLoading } = useGlobalSettingsSystemConnectItems();
	const { data: gresData, loading: gresLoading } = useResource(connectGeneralResources);

	const { sections: fgSections, loading: fgLoading } = useForgeGlobalPageItems();

	// CHOOK-247 - move to 'servicedesk-cmdb-object-schema' route in packages/routes/src/ui/spa/servicedesk/cmdb/index.js
	// once new page is rolled out. Hard-coded here since it isn't possible to have the Route navigation.sidebar
	// property change dynamically at runtime.
	if (shouldRenderInsightObjectSchemaSidebar) {
		return children(<AsyncSidebarInsightObjectSchema />);
	}
	if (isInsightMenuSelected) {
		// fullscreen connect with no sidebar
		return null;
	}

	if (sidebar === AsyncSidebarPlan) {
		return children(<AsyncSidebarPlan />);
	}

	if (sidebar === AsyncSidebarForgeGlobalPage) {
		if (fgLoading) return children(<Skeleton isFull />);
		if (fgSections?.length) return children(<AsyncSidebarForgeGlobalPage />);
		return children(null);
	}

	if (!Array.isArray(sidebar)) {
		const Sidebar = sidebar;
		return children(Sidebar != null ? <Sidebar /> : null);
	}

	if (sidebar === genericConnectSidebars) {
		const foundInProject =
			pSelected ||
			prSelected ||
			psSelected ||
			['jiraProjectAdminTabPanels'].includes(gresData?.moduleType);
		const foundSomewhere = foundInProject || gSelected || gsSelected || gssSelected;
		const stillLoading =
			pLoading || prLoading || psLoading || gLoading || gsLoading || gssLoading || gresLoading;

		if (stillLoading && !foundSomewhere) {
			return children(<Skeleton isFull />);
		}

		if (foundInProject) {
			const projectSidebar = <ProjectSidebar {...projectState} />;
			return children(projectSidebar);
		}

		if (gSelected) {
			return children(<AsyncSidebarConnect />);
		}

		if (gsSelected || ['configurePage', 'postInstallPage'].includes(gresData?.moduleType)) {
			return children(<AsyncSidebarGlobalSettingsApps />);
		}

		if (gssSelected || ['adminPages'].includes(gresData?.moduleType)) {
			return children(<AsyncSidebarGlobalSettingsSystem />);
		}

		// not found, assume fullscreen connect with no sidebar
		return children(null);
	}

	const projectSidebar = <ProjectSidebar {...projectState} />;

	return children(projectSidebar);
};
